import { useRef, useState, useId, useEffect, useCallback } from 'react';
import { useFloating, autoUpdate, offset, flip, shift, useHover, useFocus, useClick, useDismiss, useRole, useInteractions, FloatingArrow, arrow } from '@floating-ui/react';
import { TipHover, TipBubble } from './Tooltip.styled';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * The `Tooltip` atom renders a small, floating text box that provides
 * additional information about an element when the user hovers over it,
 * clicks/taps it, or focuses it with the keyboard.
 */
export const Tooltip = ({
  text,
  placement = 'bottom',
  children,
  arrowAnchorRef,
  onOpenChange,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tipId = useId();
  const arrowRef = useRef(null);
  const isTopOrBottomPlaced = placement.search('top') > -1 || placement.search('bottom') > -1;
  const arrowPadding = isTopOrBottomPlaced ? 40 : 10;
  const handleOpenChange = useCallback(open => {
    setIsOpen(open);
    onOpenChange?.(open);
  }, [onOpenChange]);
  const {
    x,
    y,
    strategy,
    refs,
    context
  } = useFloating({
    open: isOpen,
    onOpenChange: handleOpenChange,
    middleware: [offset(7), flip(), shift(), arrow({
      element: arrowRef,
      padding: arrowPadding
    })],
    whileElementsMounted: autoUpdate,
    placement
  });
  const hover = useHover(context, {
    move: false
  });
  const focus = useFocus(context);
  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, {
    role: 'tooltip'
  });

  // Merge all the interactions into prop getters
  const {
    getReferenceProps,
    getFloatingProps
  } = useInteractions([hover, focus, click, dismiss, role]);

  /**
   * When arrowAnchorRef is provided, use it as the position reference
   * This makes the tooltip and arrow position relative to that element
   * while keeping hover interactions on the full children area
   */
  useEffect(() => {
    if (arrowAnchorRef?.current) {
      refs.setPositionReference(arrowAnchorRef.current);
    }
  }, [arrowAnchorRef, refs]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(TipHover, {
      ref: refs.setReference,
      ...getReferenceProps(),
      tabIndex: 0,
      role: "button",
      ...rest,
      children: children
    }), isOpen && /*#__PURE__*/_jsxs(TipBubble, {
      ref: refs.setFloating,
      id: tipId,
      position: strategy,
      top: y ? `${y}px` : 0,
      left: x ? `${x}px` : 0,
      ...getFloatingProps(),
      children: [/*#__PURE__*/_jsx(FloatingArrow, {
        ref: arrowRef,
        context: context,
        fill: "#312f2d"
      }), text]
    })]
  });
};